// Origini fidate per i messaggi dell'iframe di login /iLogin (#817).
//
// Dopo l'accesso /iLogin manda alla pagina che lo contiene un messaggio con
// id, nome, cognome, email e telefono dell'utente. Con targetOrigin '*'
// qualunque sito che incorpora https://immaginacasa.com/iLogin li riceveva
// (art. 32 GDPR). Ora il messaggio parte solo verso un'origine di questo
// elenco, e il browser lo consegna solo se la pagina che contiene l'iframe ha
// davvero quell'origine.
//
// Chi incorpora /iLogin oggi:
// - le pagine del sito stesso (Home, annunci, FAQ, Agenzie, Vendi, Prenota
//   videochiamata, pagine legali): coperte dall'origine del sito;
// - newarc.it (sito di Newarc S.r.l., stesso titolare): l'header apre
//   https://compra.newarc.it/iLogin, che reindirizza a immaginacasa.com/iLogin,
//   e usa FIREBASELOGINSUCCEESS / FIREBASELOGINCLOSE per mostrare l'accesso.

export const TRUSTED_EMBEDDER_ORIGINS = Object.freeze([
  'https://immaginacasa.com',
  'https://www.immaginacasa.com',
  // Staging: progetto Firebase newarc-staging (.firebaserc).
  'https://newarc-staging.web.app',
  'https://newarc-staging.firebaseapp.com',
  // Sito Newarc: iframe di login nell'header.
  'https://newarc.it',
  'https://www.newarc.it',
]);

// Sviluppo locale, su qualunque porta.
const LOCAL_ORIGIN = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/;

function ownOrigin(win) {
  try {
    return (win && win.location && win.location.origin) || '';
  } catch (e) {
    return '';
  }
}

/** true se `origin` può ricevere i messaggi di /iLogin. */
export function isTrustedOrigin(origin, win = window) {
  if (!origin || typeof origin !== 'string' || origin === 'null') return false;
  if (origin === ownOrigin(win)) return true;
  if (TRUSTED_EMBEDDER_ORIGINS.includes(origin)) return true;
  return LOCAL_ORIGIN.test(origin);
}

function originOf(url) {
  try {
    const origin = new URL(url).origin;
    return origin && origin !== 'null' ? origin : '';
  } catch (e) {
    return '';
  }
}

/**
 * Origine della pagina che contiene questo documento ('' se non si sa).
 * Fuori da un iframe è l'origine del documento stesso.
 */
export function parentOrigin(win = window) {
  if (!win || !win.parent || win.parent === win) return ownOrigin(win);
  // Pagina madre della stessa origine: si legge direttamente.
  try {
    const origin = win.parent.location.origin;
    if (origin && origin !== 'null') return origin;
  } catch (e) {
    /* origine diversa: il browser non la espone, si passa alle alternative */
  }
  const ancestors = win.location && win.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0 && ancestors[0] && ancestors[0] !== 'null') {
    return ancestors[0];
  }
  const referrer = win.document && win.document.referrer;
  return referrer ? originOf(referrer) : '';
}

/** Origine a cui /iLogin può scrivere, o null se la pagina madre non è fidata. */
export function trustedParentOrigin(win = window) {
  const origin = parentOrigin(win);
  return isTrustedOrigin(origin, win) ? origin : null;
}

/**
 * Manda `message` alla pagina madre solo se `targetOrigin` è fidata.
 * Restituisce true se il messaggio è partito.
 */
export function postToTrustedParent(message, targetOrigin, win = window) {
  if (!win || !win.parent || !isTrustedOrigin(targetOrigin, win)) return false;
  win.parent.postMessage(message, targetOrigin);
  return true;
}

/** true se l'evento `message` arriva dalla stessa origine della pagina. */
export function isSameOriginMessage(event, win = window) {
  return !!event && !!event.origin && event.origin === ownOrigin(win);
}

// #3234: il brand di chi incorpora /iLogin. L'header di newarc.it apre
// compra.newarc.it/iLogin, che reindirizza a immaginacasa.com/iLogin: dentro
// l'iframe currentBrand() direbbe 'immaginacasa' e un cliente solo Newarc
// riceverebbe il rapporto con Immaginacasa (registrazione, login).
const NEWARC_EMBEDDER_ORIGINS = Object.freeze(['https://newarc.it', 'https://www.newarc.it']);
const IMMAGINACASA_EMBEDDER_ORIGINS = Object.freeze(['https://immaginacasa.com', 'https://www.immaginacasa.com']);

/** true se il documento gira dentro un iframe (anche se non si può leggere `top`). */
export function isEmbedded(win = window) {
  try {
    return !!win && win.top !== win.self;
  } catch (e) {
    return true;
  }
}

/**
 * Brand della pagina che incorpora questo documento: 'newarc' | 'immaginacasa',
 * o null quando non si sa in modo affidabile (origine non fidata, non
 * determinabile, sviluppo locale, staging). La pagina madre della stessa
 * origine (il modale di login del sito) ha il brand dell'host, come currentBrand().
 */
export function embedderBrand(win = window) {
  const origin = trustedParentOrigin(win);
  if (!origin) return null;
  if (NEWARC_EMBEDDER_ORIGINS.includes(origin)) return 'newarc';
  if (IMMAGINACASA_EMBEDDER_ORIGINS.includes(origin)) return 'immaginacasa';
  if (origin === ownOrigin(win)) {
    try {
      return /newarc/i.test(new URL(origin).hostname) ? 'newarc' : 'immaginacasa';
    } catch (e) {
      return null;
    }
  }
  return null;
}
